import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ImportContacts from "./ImportContacts";
import {
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaPlus,
  FaUsers,
  FaSave,
  FaTimes,
  FaCalendarAlt,
} from "react-icons/fa";
import CreateContact from "./CreateContact";
import {
  deleteSegmentSuccess,
  updateSegmentSuccess,
  setError,
  setLoading,
  setSegments,
} from "../../../../state/reducers/communicationSlice";
import {
  deleteSegment,
  updateSegment,
} from "../../../../services/communication.services";
import UploadExcelFile from "./UploadExcelFile";
import { formatDateTime } from "../../../../utils/dateFormat";

const ManageSegments = ({ fetchContacts, segments, onBack }) => {
  const [showImportContacts, setShowImportContacts] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [updatedSegmentName, setUpdatedSegmentName] = useState("");
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const dispatch = useDispatch();

  // Check if screen is mobile
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleCreateSegment = () => {
    setShowImportContacts(true);
  };

  const handleUpdateSegment = async (segment__id, updatedData) => {
    try {
      dispatch(setLoading(true));
      const updatedSegment = await updateSegment(segment__id, updatedData);
      dispatch(updateSegmentSuccess(updatedSegment, segment__id));
      const updatedSegments = segments.map((item) =>
        item.segment__id === updatedSegment.segment.id
          ? { ...item, segment__name: updatedSegment.segment.name }
          : item
      );
      dispatch(setSegments(updatedSegments));
      setIsEditing(null);
    } catch (error) {
      dispatch(setError(error.message));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDeleteSegment = async (segment__id) => {
    if (!window.confirm("Are you sure you want to delete this segment?")) {
      return;
    }

    try {
      const res = await deleteSegment(segment__id);
      dispatch(deleteSegmentSuccess(segment__id));
      const filtered = segments.filter(
        (item) => item.segment__id !== segment__id
      );
      dispatch(setSegments(filtered));
    } catch (error) {
      dispatch(setError(error.message));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAddContactClick = (segment__id) => {
    setSelectedSegmentId(segment__id);
    setShowCreateContact(true);
  };

  const handleCloseCreateContact = () => {
    setShowCreateContact(false);
  };

  const handleEditClick = (segment) => {
    setIsEditing(segment.segment__id);
    setUpdatedSegmentName(segment.segment__name);
  };

  const handleCancelEdit = () => {
    setIsEditing(null);
    setUpdatedSegmentName("");
  };

  const handleSaveClick = (segment__id) => {
    if (!updatedSegmentName.trim()) {
      alert("Segment name cannot be empty");
      return;
    }
    const updatedData = { segment_name: updatedSegmentName };
    handleUpdateSegment(segment__id, updatedData);
  };

  if (showImportContacts) {
    return (
      <CreateContact
        fetchContacts={fetchContacts}
        onClose={() => setShowImportContacts(false)}
      />
    );
  }

  if (showCreateContact) {
    return (
      <UploadExcelFile
        fetchContacts={fetchContacts}
        onClose={handleCloseCreateContact}
        selectedSegmentId={selectedSegmentId}
      />
    );
  }

  // Mobile Segment Card Component
  const SegmentCard = ({ segment, index }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <span className="text-sm text-gray-500 mr-2">#{index + 1}</span>
            <FaUsers className="text-gray-400 text-sm" />
          </div>

          {isEditing === segment.segment__id ? (
            <div className="mb-3">
              <input
                type="text"
                value={updatedSegmentName}
                onChange={(e) => setUpdatedSegmentName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter segment name"
              />
            </div>
          ) : (
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {segment.segment__name}
            </h3>
          )}
        </div>

        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {segment.total_contacts}
          </div>
          <div className="text-xs text-gray-500">contacts</div>
        </div>
      </div>

      {/* Last Modified */}
      <div className="flex items-center text-sm text-gray-600 mb-4">
        <FaCalendarAlt className="mr-2" />
        <span>Modified: {formatDateTime(segment.updated_at) || "N/A"}</span>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col space-y-2">
        {isEditing === segment.segment__id ? (
          <div className="flex space-x-2">
            <button
              onClick={() => handleSaveClick(segment.segment__id)}
              className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md text-sm hover:bg-green-700 transition-colors flex items-center justify-center"
            >
              <FaSave className="mr-1" />
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="flex-1 bg-gray-500 text-white px-3 py-2 rounded-md text-sm hover:bg-gray-600 transition-colors flex items-center justify-center"
            >
              <FaTimes className="mr-1" />
              Cancel
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={() => handleAddContactClick(segment.segment__id)}
              className="w-full bg-purple-600 text-white px-3 py-2 rounded-md text-sm hover:bg-purple-700 transition-colors flex items-center justify-center"
            >
              <FaPlus className="mr-1" />
              Add Contacts
            </button>
            <div className="flex space-x-2">
              <button
                onClick={() => handleEditClick(segment)}
                className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-md text-sm hover:bg-blue-100 transition-colors flex items-center justify-center"
              >
                <FaEdit className="mr-1" />
                Edit
              </button>
              <button
                onClick={() => handleDeleteSegment(segment.segment__id)}
                className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded-md text-sm hover:bg-red-100 transition-colors flex items-center justify-center"
              >
                <FaTrash className="mr-1" />
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`min-h-screen bg-gray-50 transition-all duration-300 ${
        isSideBarOpen ? "ml-0 lg:ml-80" : "ml-0 md:ml-20"
      }`}
    >
      <div className="p-4 sm:p-6">
        {/* Header Section */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors mb-4 flex items-center gap-2 text-sm sm:text-base"
          >
            <FaArrowLeft />
            Back to Contacts
          </button>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800">
                Manage Segments
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Create, edit, and organize your contact segments
              </p>
            </div>
            <button
              onClick={handleCreateSegment}
              className="w-full sm:w-auto bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
            >
              <FaPlus className="mr-2" />
              Create Segment
            </button>
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {segments.length === 0 ? (
            <div className="text-center py-12">
              <FaUsers className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No segments yet
              </h3>
              <p className="text-gray-600 mb-4">
                Create your first segment to organize your contacts
              </p>
              <button
                onClick={handleCreateSegment}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <FaPlus className="mr-2 inline" />
                Create First Segment
              </button>
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="block md:hidden p-4">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {segments.length} Segment{segments.length !== 1 ? "s" : ""}
                  </h3>
                </div>
                {segments.map((segment, index) => (
                  <SegmentCard
                    key={segment.segment__id}
                    segment={segment}
                    index={index}
                  />
                ))}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Segment Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contacts
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Modified
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Add Contacts
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {segments.map((segment, index) => (
                      <tr
                        key={segment.segment__id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {isEditing === segment.segment__id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={updatedSegmentName}
                                onChange={(e) =>
                                  setUpdatedSegmentName(e.target.value)
                                }
                                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 min-w-0 flex-1"
                                placeholder="Enter segment name"
                              />
                            </div>
                          ) : (
                            <div className="text-sm font-medium text-gray-900">
                              {segment.segment__name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FaUsers className="text-gray-400 mr-2" />
                            <span className="text-sm font-semibold text-gray-900">
                              {segment.total_contacts}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <FaCalendarAlt className="mr-2" />
                            {formatDateTime(segment.updated_at) || "N/A"}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            onClick={() =>
                              handleAddContactClick(segment.segment__id)
                            }
                            className="text-purple-600 hover:text-purple-700 font-medium text-sm transition-colors"
                          >
                            Add Contacts
                          </button>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {isEditing === segment.segment__id ? (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  handleSaveClick(segment.segment__id)
                                }
                                className="bg-green-100 text-green-700 px-3 py-1 rounded-md hover:bg-green-200 transition-colors flex items-center"
                              >
                                <FaSave className="mr-1" />
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-md hover:bg-gray-200 transition-colors flex items-center"
                              >
                                <FaTimes className="mr-1" />
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => handleEditClick(segment)}
                                className="text-blue-600 hover:text-blue-700 transition-colors flex items-center"
                              >
                                <FaEdit className="mr-1" />
                                Edit
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteSegment(segment.segment__id)
                                }
                                className="text-red-600 hover:text-red-700 transition-colors flex items-center"
                              >
                                <FaTrash className="mr-1" />
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageSegments;
